import * as THREE from 'three';

export class SmartPlateViewer {
  public isAdmin;
  public text;
  public imagePath;
  public fontPath;
  public width;
  public height;
  public savedPositionData;
  public camera;
  public scene;
  public renderer: THREE.WebGLRenderer;
  public cameraPosition = [0, 0, 100];
  public cameraTarget;
  public boxMesh;
  public targetRotation = 0;
  public targetRotationOnPointerDown = 0;
  public pointerXOnPointerDown = 0;
  public windowHalfX;
  public raycaster = new THREE.Raycaster();
  public point = new THREE.Vector2();
  public useDrag = true;
  public container = null;
  public fontColour: string;
  public drawLogo: boolean = false;

  constructor() {
    // try and load font
  }

  public dispose(): void {
    this.renderer.dispose();
  }

  public startWithPosition(params, callback: (src: string) => void): void {
    this.useDrag = false;

    if (params !== null) {
      this.isAdmin = params.isAdmin;
      this.text = params.text;
      this.imagePath = params.imageInfo.path;
      this.fontPath = params.fontPath;
      this.width = params.imageInfo._width;
      this.height = params.imageInfo._height;
      this.fontColour = params.textInfo.color;

      this.savedPositionData = params;
    }

    this.init(() => {
      // complete
      callback(this.getImage());
      this.renderer.domElement.remove();
    });
  }

  public init(callback: () => void): void {
    if (THREE == null) {
      console.error('three.js import is null');
      return;
    }
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    if (this.renderer == null) {
      console.error('three.js is null');
      return;
    }

    //this.container = document.getElementById('smart-plate-viewer-container');
    var canvasContainer = document.createElement('div');
    canvasContainer.style.display = 'none';
    this.container = canvasContainer;
    this.windowHalfX = this.width / 2;

    THREE.ColorManagement.enabled = false;
    this.renderer.outputColorSpace = THREE.LinearSRGBColorSpace;

    this.renderer.setSize(this.width, this.height);
    this.container.appendChild(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera();

    this.camera.position.set(...this.cameraPosition);

    this.cameraTarget = new THREE.Vector3(0, 0, 0);
    this.camera.lookAt(this.cameraTarget);

    this.scene = new THREE.Scene();

    const textureBackground = new THREE.TextureLoader().load(
      this.imagePath,
      () => {
        this.scene.background = textureBackground;
        this.createText(callback);

        // EVENTS
        this.container.style.touchAction = 'none';
      }
    );
  }

  public createText(callback: () => void, attempt = 0): void {
    var textCanvas = document.createElement('canvas');
    var context = textCanvas.getContext('2d');

    /*
    Add logo
    */
    const logoTexture = new THREE.TextureLoader().load(
      '/assets/white_logo.png',
      () => {
        textCanvas.width = 600;
        textCanvas.height = 60;

        if (!document.fonts.check('60px UKReg') && attempt < 10) {
          console.warn('font unloaded', attempt);
          setTimeout(() => {
            this.createText(callback, attempt + 1);
          }, 1000);
          return;
        }

        if (attempt >= 10) {
          console.error('Font was not loaded, incorrect image');
        }

        context.textAlign = 'center';
        context.font = '60px UKReg';
        context.fillStyle = this.fontColour;

        var __ = context.measureText('WWW WWWW');
        context.fillText(this.text, 300, (60 + 40) / 2);

        const canvasTexture = new THREE.Texture(textCanvas);
        canvasTexture.needsUpdate = true;
        const canvasGeometry = new THREE.PlaneGeometry(__.width, 40);
        canvasGeometry.computeBoundingBox();
        canvasGeometry.center();

        this.boxMesh = new THREE.Mesh(
          canvasGeometry,
          new THREE.MeshBasicMaterial({
            map: canvasTexture,
            side: THREE.DoubleSide,
            transparent: true,
          })
        );
        this.scene.add(this.boxMesh);

        if (typeof this.savedPositionData !== 'undefined') {
          this.boxMesh.position.set(
            parseFloat(this.savedPositionData.textInfo.position.x),
            parseFloat(this.savedPositionData.textInfo.position.y),
            parseFloat(this.savedPositionData.textInfo.position.z)
          );

          this.boxMesh.scale.set(
            parseFloat(this.savedPositionData.textInfo.scale.x),
            parseFloat(this.savedPositionData.textInfo.scale.y),
            parseFloat(this.savedPositionData.textInfo.scale.z)
          );

          this.boxMesh.rotation.x = parseFloat(
            this.savedPositionData.textInfo.rotation._x
          );
          this.boxMesh.rotation.y = parseFloat(
            this.savedPositionData.textInfo.rotation._y
          );
          this.boxMesh.rotation.z = parseFloat(
            this.savedPositionData.textInfo.rotation._z
          );
        }

        logoTexture.needsUpdate = true;
        const logoGeometry = new THREE.PlaneGeometry(100, 35);
        logoGeometry.computeBoundingBox();
        logoGeometry.center();

        const logoMesh = new THREE.Mesh(
          logoGeometry,
          new THREE.MeshBasicMaterial({
            map: logoTexture,
            side: THREE.DoubleSide,
            transparent: true,
          })
        );

        logoMesh.position.z = 1;
        logoMesh.position.y = -((this.height / 2) * 0.1) + 22;

        logoMesh.scale.setScalar(0.15);

        if (this.drawLogo) {
          this.scene.add(logoMesh);
        }

        this.render(callback);
      }
    );
  }

  public render(callback: () => void = () => {}): void {
    this.renderer.clear();
    this.renderer.render(this.scene, this.camera);
    callback();
  }

  public getImage = function () {
    this.renderer.render(this.scene, this.camera);
    return this.renderer.domElement.toDataURL('image/jpeg', 0.75);
  };

  // ---------------------------------------------------------------------------
  // PROOF OF CONCEPT — plateZoomCrop (not wired into production)
  // Derives a zoom-crop rectangle from the SAME placed plate mesh, by projecting
  // the rendered plate's bounding-box corners through the SAME camera. No new
  // stored config and no manual per-vehicle coordinates are required.
  // ---------------------------------------------------------------------------

  public computePlateZoomCropRect(
    targetMultiplier: number = 4,
    // Defaults to the rendered image's own aspect (1920x1200 -> 1.6 / 8:5) so
    // the crop reads as the SAME image zoomed in, not a different tile format.
    aspect: number = this.width / this.height
  ): {
    crop: { x: number; y: number; w: number; h: number };
    plate: { x: number; y: number; w: number; h: number };
    center: { x: number; y: number };
    multiplier: number;
    aspect: number;
    clampedToBounds: boolean;
  } {
    if (this.boxMesh == null) {
      throw new Error('computePlateZoomCropRect: plate mesh not built yet');
    }

    // Project the plate plane's 8 bbox corners to output pixels.
    this.boxMesh.updateMatrixWorld(true);
    const geo = this.boxMesh.geometry;
    geo.computeBoundingBox();
    const bb = geo.boundingBox;
    const corners = [
      new THREE.Vector3(bb.min.x, bb.min.y, bb.min.z),
      new THREE.Vector3(bb.min.x, bb.min.y, bb.max.z),
      new THREE.Vector3(bb.min.x, bb.max.y, bb.min.z),
      new THREE.Vector3(bb.min.x, bb.max.y, bb.max.z),
      new THREE.Vector3(bb.max.x, bb.min.y, bb.min.z),
      new THREE.Vector3(bb.max.x, bb.min.y, bb.max.z),
      new THREE.Vector3(bb.max.x, bb.max.y, bb.min.z),
      new THREE.Vector3(bb.max.x, bb.max.y, bb.max.z),
    ];

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const v = new THREE.Vector3();
    for (const c of corners) {
      v.copy(c).applyMatrix4(this.boxMesh.matrixWorld).project(this.camera);
      const px = (v.x * 0.5 + 0.5) * this.width;
      const py = (1 - (v.y * 0.5 + 0.5)) * this.height; // flip Y to image space
      if (px < minX) minX = px;
      if (px > maxX) maxX = px;
      if (py < minY) minY = py;
      if (py > maxY) maxY = py;
    }

    const plateW = maxX - minX;
    const plateH = maxY - minY;
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;

    // Build an aspect-locked crop box that fully contains the plate plus
    // `targetMultiplier` padding, then size it to `aspect`. Taking the max of
    // both axes guarantees the plate stays fully visible (cropW >= plateW and
    // cropH >= plateH for multiplier >= 1) while keeping the requested aspect.
    const padW = plateW * targetMultiplier;
    const padH = plateH * targetMultiplier;
    let w = Math.max(padW, padH * aspect, plateW, plateH * aspect);
    let h = w / aspect;

    // Cap to the largest aspect-correct box that fits inside the image.
    let clampedToBounds = false;
    const maxW = Math.min(this.width, this.height * aspect);
    if (w > maxW) {
      w = maxW;
      h = w / aspect;
      clampedToBounds = true;
    }

    // Centre on the plate; shift the centre only as far as clamping requires.
    let x = cx - w / 2;
    let y = cy - h / 2;
    if (x < 0) {
      x = 0;
      clampedToBounds = true;
    } else if (x + w > this.width) {
      x = this.width - w;
      clampedToBounds = true;
    }
    if (y < 0) {
      y = 0;
      clampedToBounds = true;
    } else if (y + h > this.height) {
      y = this.height - h;
      clampedToBounds = true;
    }

    return {
      crop: {
        x: Math.round(x),
        y: Math.round(y),
        w: Math.round(w),
        h: Math.round(h),
      },
      plate: {
        x: Math.round(minX),
        y: Math.round(minY),
        w: Math.round(plateW),
        h: Math.round(plateH),
      },
      center: { x: Math.round(cx), y: Math.round(cy) },
      multiplier: targetMultiplier,
      aspect: Math.round(aspect * 1000) / 1000,
      clampedToBounds,
    };
  }

  public getZoomCropImage(
    targetMultiplier: number = 4,
    aspect?: number
  ): {
    full: string;
    crop: string;
    rect: ReturnType<SmartPlateViewer['computePlateZoomCropRect']>;
  } {
    // WebGL buffer is only valid right after a render, so render then read.
    this.renderer.render(this.scene, this.camera);
    const full = this.renderer.domElement.toDataURL('image/jpeg', 0.9);

    const rect = this.computePlateZoomCropRect(
      targetMultiplier,
      aspect ?? this.width / this.height
    );

    const out = document.createElement('canvas');
    out.width = rect.crop.w;
    out.height = rect.crop.h;
    const ctx = out.getContext('2d');
    ctx.drawImage(
      this.renderer.domElement,
      rect.crop.x,
      rect.crop.y,
      rect.crop.w,
      rect.crop.h,
      0,
      0,
      rect.crop.w,
      rect.crop.h
    );
    const crop = out.toDataURL('image/jpeg', 0.9);

    return { full, crop, rect };
  }

  // ---------------------------------------------------------------------------
  // PROOF OF CONCEPT — TRUE-ZOOM variant (not wired into production)
  // `zoom` is linear MAGNIFICATION relative to the full render: the crop spans
  // 1/zoom of the source, so the plate content appears `zoom`x larger (3 => the
  // plate is 3x bigger, a closer view). This is the opposite axis to
  // computePlateZoomCropRect, whose `targetMultiplier` is a context-padding
  // factor that zooms OUT (shows more bodywork) as it grows.
  // ---------------------------------------------------------------------------
  public computePlateZoomCropRectByZoom(
    zoom: number = 3,
    aspect: number = this.width / this.height
  ): {
    crop: { x: number; y: number; w: number; h: number };
    plate: { x: number; y: number; w: number; h: number };
    center: { x: number; y: number };
    zoom: number;
    aspect: number;
    clampedToBounds: boolean;
  } {
    if (this.boxMesh == null) {
      throw new Error('computePlateZoomCropRectByZoom: plate mesh not built yet');
    }

    // Project the plate plane's 8 bbox corners to output pixels (same as the
    // multiplier-based method, so placement is identical).
    this.boxMesh.updateMatrixWorld(true);
    const geo = this.boxMesh.geometry;
    geo.computeBoundingBox();
    const bb = geo.boundingBox;
    const corners = [
      new THREE.Vector3(bb.min.x, bb.min.y, bb.min.z),
      new THREE.Vector3(bb.min.x, bb.min.y, bb.max.z),
      new THREE.Vector3(bb.min.x, bb.max.y, bb.min.z),
      new THREE.Vector3(bb.min.x, bb.max.y, bb.max.z),
      new THREE.Vector3(bb.max.x, bb.min.y, bb.min.z),
      new THREE.Vector3(bb.max.x, bb.min.y, bb.max.z),
      new THREE.Vector3(bb.max.x, bb.max.y, bb.min.z),
      new THREE.Vector3(bb.max.x, bb.max.y, bb.max.z),
    ];

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const v = new THREE.Vector3();
    for (const c of corners) {
      v.copy(c).applyMatrix4(this.boxMesh.matrixWorld).project(this.camera);
      const px = (v.x * 0.5 + 0.5) * this.width;
      const py = (1 - (v.y * 0.5 + 0.5)) * this.height; // flip Y to image space
      if (px < minX) minX = px;
      if (px > maxX) maxX = px;
      if (py < minY) minY = py;
      if (py > maxY) maxY = py;
    }

    const plateW = maxX - minX;
    const plateH = maxY - minY;
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;

    // Crop spans 1/zoom of the render, locked to `aspect`.
    let w = this.width / zoom;
    let h = w / aspect;

    // Never crop tighter than the plate itself: keep the registration fully
    // visible even at high zoom.
    if (w < plateW) {
      w = plateW;
      h = w / aspect;
    }
    if (h < plateH) {
      h = plateH;
      w = h * aspect;
    }

    // Cap to the largest aspect-correct box that fits inside the image.
    let clampedToBounds = false;
    const maxW = Math.min(this.width, this.height * aspect);
    if (w > maxW) {
      w = maxW;
      h = w / aspect;
      clampedToBounds = true;
    }

    // Centre on the plate; shift only as far as clamping requires.
    let x = cx - w / 2;
    let y = cy - h / 2;
    if (x < 0) {
      x = 0;
      clampedToBounds = true;
    } else if (x + w > this.width) {
      x = this.width - w;
      clampedToBounds = true;
    }
    if (y < 0) {
      y = 0;
      clampedToBounds = true;
    } else if (y + h > this.height) {
      y = this.height - h;
      clampedToBounds = true;
    }

    return {
      crop: {
        x: Math.round(x),
        y: Math.round(y),
        w: Math.round(w),
        h: Math.round(h),
      },
      plate: {
        x: Math.round(minX),
        y: Math.round(minY),
        w: Math.round(plateW),
        h: Math.round(plateH),
      },
      center: { x: Math.round(cx), y: Math.round(cy) },
      zoom,
      aspect: Math.round(aspect * 1000) / 1000,
      clampedToBounds,
    };
  }

  public getZoomCropImageByZoom(
    zoom: number = 3,
    aspect?: number
  ): {
    full: string;
    crop: string;
    rect: ReturnType<SmartPlateViewer['computePlateZoomCropRectByZoom']>;
  } {
    this.renderer.render(this.scene, this.camera);
    const full = this.renderer.domElement.toDataURL('image/jpeg', 0.9);

    const rect = this.computePlateZoomCropRectByZoom(
      zoom,
      aspect ?? this.width / this.height
    );

    const out = document.createElement('canvas');
    out.width = rect.crop.w;
    out.height = rect.crop.h;
    const ctx = out.getContext('2d');
    ctx.drawImage(
      this.renderer.domElement,
      rect.crop.x,
      rect.crop.y,
      rect.crop.w,
      rect.crop.h,
      0,
      0,
      rect.crop.w,
      rect.crop.h
    );
    const crop = out.toDataURL('image/jpeg', 0.9);

    return { full, crop, rect };
  }

  // ---------------------------------------------------------------------------
  // PROOF OF CONCEPT — CONTENT-AWARE crop (not wired into production)
  // Sizes the crop from the plate's own projected width so the registration
  // fills a target fraction of the crop, regardless of how wide/narrow the
  // plate projects per template (fixed multipliers are not robust across the
  // fleet). cropWidth = projectedPlateWidth / targetFill; cropHeight follows the
  // source render aspect; both grow together if the plate needs more vertical
  // breathing room; centred on the plate; clamped inside the render.
  // ---------------------------------------------------------------------------
  public computePlateZoomCropRectByFill(
    targetFill: number = 0.48,
    aspect: number = this.width / this.height
  ): {
    crop: { x: number; y: number; w: number; h: number };
    plate: { x: number; y: number; w: number; h: number };
    center: { x: number; y: number };
    targetFill: number;
    actualFillW: number;
    actualFillH: number;
    aspect: number;
    clampedToBounds: boolean;
    breathingAdjusted: boolean;
  } {
    if (this.boxMesh == null) {
      throw new Error('computePlateZoomCropRectByFill: plate mesh not built yet');
    }

    // Project the plate plane's 8 bbox corners to output pixels.
    this.boxMesh.updateMatrixWorld(true);
    const geo = this.boxMesh.geometry;
    geo.computeBoundingBox();
    const bb = geo.boundingBox;
    const corners = [
      new THREE.Vector3(bb.min.x, bb.min.y, bb.min.z),
      new THREE.Vector3(bb.min.x, bb.min.y, bb.max.z),
      new THREE.Vector3(bb.min.x, bb.max.y, bb.min.z),
      new THREE.Vector3(bb.min.x, bb.max.y, bb.max.z),
      new THREE.Vector3(bb.max.x, bb.min.y, bb.min.z),
      new THREE.Vector3(bb.max.x, bb.min.y, bb.max.z),
      new THREE.Vector3(bb.max.x, bb.max.y, bb.min.z),
      new THREE.Vector3(bb.max.x, bb.max.y, bb.max.z),
    ];

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const v = new THREE.Vector3();
    for (const c of corners) {
      v.copy(c).applyMatrix4(this.boxMesh.matrixWorld).project(this.camera);
      const px = (v.x * 0.5 + 0.5) * this.width;
      const py = (1 - (v.y * 0.5 + 0.5)) * this.height; // flip Y to image space
      if (px < minX) minX = px;
      if (px > maxX) maxX = px;
      if (py < minY) minY = py;
      if (py > maxY) maxY = py;
    }

    const plateW = maxX - minX;
    const plateH = maxY - minY;
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;

    // 1) Size the crop so the plate fills `targetFill` of the crop width.
    let w = plateW / targetFill;
    let h = w / aspect;

    // 2) Vertical breathing room: never let the plate occupy more than
    //    VERTICAL_MAX_FILL of the crop height. If it would, grow w & h together
    //    (preserving aspect) until there is room above and below the plate.
    const VERTICAL_MAX_FILL = 0.8;
    let breathingAdjusted = false;
    if (plateH / h > VERTICAL_MAX_FILL) {
      h = plateH / VERTICAL_MAX_FILL;
      w = h * aspect;
      breathingAdjusted = true;
    }

    // 3) Cap to the largest aspect-correct box that fits inside the render.
    let clampedToBounds = false;
    const maxW = Math.min(this.width, this.height * aspect);
    if (w > maxW) {
      w = maxW;
      h = w / aspect;
      clampedToBounds = true;
    }

    // 4) Centre on the plate; shift only as far as clamping requires so the
    //    plate stays as close to centre as possible.
    let x = cx - w / 2;
    let y = cy - h / 2;
    if (x < 0) {
      x = 0;
      clampedToBounds = true;
    } else if (x + w > this.width) {
      x = this.width - w;
      clampedToBounds = true;
    }
    if (y < 0) {
      y = 0;
      clampedToBounds = true;
    } else if (y + h > this.height) {
      y = this.height - h;
      clampedToBounds = true;
    }

    return {
      crop: {
        x: Math.round(x),
        y: Math.round(y),
        w: Math.round(w),
        h: Math.round(h),
      },
      plate: {
        x: Math.round(minX),
        y: Math.round(minY),
        w: Math.round(plateW),
        h: Math.round(plateH),
      },
      center: { x: Math.round(cx), y: Math.round(cy) },
      targetFill,
      actualFillW: Math.round((plateW / w) * 1000) / 1000,
      actualFillH: Math.round((plateH / h) * 1000) / 1000,
      aspect: Math.round(aspect * 1000) / 1000,
      clampedToBounds,
      breathingAdjusted,
    };
  }

  public getZoomCropImageByFill(
    targetFill: number = 0.48,
    aspect?: number
  ): {
    full: string;
    crop: string;
    rect: ReturnType<SmartPlateViewer['computePlateZoomCropRectByFill']>;
  } {
    this.renderer.render(this.scene, this.camera);
    const full = this.renderer.domElement.toDataURL('image/jpeg', 0.9);

    const rect = this.computePlateZoomCropRectByFill(
      targetFill,
      aspect ?? this.width / this.height
    );

    const out = document.createElement('canvas');
    out.width = rect.crop.w;
    out.height = rect.crop.h;
    const ctx = out.getContext('2d');
    ctx.drawImage(
      this.renderer.domElement,
      rect.crop.x,
      rect.crop.y,
      rect.crop.w,
      rect.crop.h,
      0,
      0,
      rect.crop.w,
      rect.crop.h
    );
    const crop = out.toDataURL('image/jpeg', 0.9);

    return { full, crop, rect };
  }
}
